/**
 * @file AbaResourcesHub.jsx
 * @description Public resources hub at /aba-resources — a clean, categorized
 *   index linking the provider authority content cluster (Phase 3B) and the
 *   related tracker pages, so these pages are discoverable through normal site
 *   navigation, not sitemap.xml alone. No content of its own beyond framing —
 *   it exists purely so a visitor (or Googlebot) can reach every guide from one
 *   linked page. Mirrors the structure of AboutAbaCases.jsx.
 * @module components/AbaResourcesHub
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  ShieldIcon, FileTextIcon, MessageIcon, MapPinIcon, UsersIcon,
  DollarIcon, BellIcon, BriefcaseIcon, ClipboardIcon,
  AwardIcon, LayersIcon, CarIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

// "New to ABA? Start here": the three guides a brand new reader should read, in order.
const START_HERE = [
  { to: "aba-roles-guide", labelKey: "abaResources.group1Item4Label", bodyKey: "abaResources.group1Item4Body" },
  { to: "how-to-become-an-rbt", labelKey: "abaResources.group1Item1Label", bodyKey: "abaResources.group1Item1Body" },
  { to: "rbt-resume-guide", labelKey: "abaResources.group2Item1Label", bodyKey: "abaResources.group2Item1Body" },
];

const ALL_GROUPS = [
  {
    titleKey: "abaResources.group1Title",
    items: [
      { Icon: ShieldIcon, to: "how-to-become-an-rbt", labelKey: "abaResources.group1Item1Label", bodyKey: "abaResources.group1Item1Body" },
      { Icon: AwardIcon, to: "how-to-become-a-bcba", labelKey: "abaResources.group1Item2Label", bodyKey: "abaResources.group1Item2Body" },
      { Icon: LayersIcon, to: "how-to-become-a-bcaba", labelKey: "abaResources.group1Item3Label", bodyKey: "abaResources.group1Item3Body" },
      { Icon: BriefcaseIcon, to: "aba-roles-guide", labelKey: "abaResources.group1Item4Label", bodyKey: "abaResources.group1Item4Body" },
      { Icon: UsersIcon, to: "rbt-vs-bt-vs-bcba-vs-bcaba", labelKey: "abaResources.group1Item5Label", bodyKey: "abaResources.group1Item5Body" },
    ],
  },
  {
    titleKey: "abaResources.group2Title",
    items: [
      { Icon: FileTextIcon, to: "rbt-resume-guide", labelKey: "abaResources.group2Item1Label", bodyKey: "abaResources.group2Item1Body" },
      { Icon: MessageIcon, to: "rbt-interview-questions", labelKey: "abaResources.group2Item2Label", bodyKey: "abaResources.group2Item2Body" },
      { Icon: MapPinIcon, to: "how-to-choose-an-aba-case", labelKey: "abaResources.group2Item3Label", bodyKey: "abaResources.group2Item3Body" },
      { Icon: CarIcon, to: "travel-rbt-jobs", labelKey: "abaResources.group2Item4Label", bodyKey: "abaResources.group2Item4Body" },
    ],
  },
  {
    titleKey: "abaResources.group3Title",
    items: [
      { Icon: DollarIcon, to: "free-aba-timesheet-payment-tracker", labelKey: "abaResources.group3Item1Label", bodyKey: "abaResources.group3Item1Body" },
      { Icon: DollarIcon, to: "aba-timesheet", labelKey: "abaResources.group3Item2Label", bodyKey: "abaResources.group3Item2Body" },
      { Icon: DollarIcon, to: "rbt-timesheet", labelKey: "abaResources.group3Item3Label", bodyKey: "abaResources.group3Item3Body" },
      { Icon: DollarIcon, to: "aba-payment-tracker", labelKey: "abaResources.group3Item4Label", bodyKey: "abaResources.group3Item4Body" },
    ],
  },
  {
    titleKey: "abaResources.group4Title",
    items: [
      { Icon: BellIcon, to: "aba-provider-waitlist", labelKey: "abaResources.group4Item1Label", bodyKey: "abaResources.group4Item1Body" },
      { Icon: MapPinIcon, to: "aba-case-finder", labelKey: "abaResources.group4Item2Label", bodyKey: "abaResources.group4Item2Body" },
    ],
  },
  {
    titleKey: "abaResources.group5Title",
    items: [
      { Icon: ClipboardIcon, to: "post-aba-cases", labelKey: "abaResources.group5Item1Label", bodyKey: "abaResources.group5Item1Body" },
      { Icon: BriefcaseIcon, to: "aba-agencies", labelKey: "abaResources.group5Item2Label", bodyKey: "abaResources.group5Item2Body" },
      { Icon: MapPinIcon, to: "rbt-staffing-charlotte-nc", labelKey: "abaResources.group5Item3Label", bodyKey: "abaResources.group5Item3Body" },
      { Icon: MapPinIcon, to: "rbt-staffing-miami-fl", labelKey: "abaResources.group5Item4Label", bodyKey: "abaResources.group5Item4Body" },
    ],
  },
  {
    titleKey: "abaResources.group6Title",
    items: [
      { Icon: MapPinIcon, to: "rbt-jobs-florida", labelKey: "abaResources.group4Item6Label", bodyKey: "abaResources.group4Item6Body" },
      { Icon: MapPinIcon, to: "rbt-jobs-miami-fl", labelKey: "abaResources.group4Item3Label", bodyKey: "abaResources.group4Item3Body" },
      { Icon: MapPinIcon, to: "rbt-jobs-charlotte-nc", labelKey: "abaResources.group4Item4Label", bodyKey: "abaResources.group4Item4Body" },
      { Icon: MapPinIcon, to: "rbt-jobs-north-carolina", labelKey: "abaResources.group4Item5Label", bodyKey: "abaResources.group4Item5Body" },
      { Icon: MapPinIcon, to: "aba-jobs-by-state", labelKey: "abaResources.group6AllStatesLabel", bodyKey: "abaResources.group6AllStatesBody" },
    ],
  },
];

// The three "Start here" guides are not listed again below; a group left with no items is dropped.
const START_HERE_TO = new Set(START_HERE.map((s) => s.to));
const GROUPS = ALL_GROUPS
  .map((g) => ({ ...g, items: g.items.filter((it) => !START_HERE_TO.has(it.to)) }))
  .filter((g) => g.items.length > 0);

export default function AbaResourcesHub({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "ABA Resources for RBTs, BTs, BCBAs and BCaBAs | ABAcases",
    description: "Guides on becoming an RBT, ABA resumes, interviews, choosing a case, and ABA roles explained, plus the free ABA timesheet, payment tracker, and case alerts.",
    canonical: "/aba-resources",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-aba-resources", [
    { name: "Home", url: "/" },
    { name: "ABA Resources", url: "/aba-resources" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/aba-resources/" },
      ga4Params: ctaMeta({
        pagePath: "/aba-resources/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 980;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          <Breadcrumbs items={[
            { label: t('abaResources.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('abaResources.breadcrumbResources') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('abaResources.badgeResources')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('abaResources.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 15.5 : 17, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 680, margin: 0 }}>
            {t('abaResources.heroSubtitle')}
          </p>
        </div>
      </header>

      {/* ── New to ABA? Start here ─────────────────────────────── */}
      <div style={{ padding: isMobile ? "28px 18px 0" : "40px 48px 0", maxWidth: maxW, margin: "0 auto" }}>
        <section data-testid="start-here" style={{ background: "#f5f3ff", border: `1px solid ${BORDER}`, borderLeft: `4px solid ${INDIGO}`, borderRadius: 14, padding: isMobile ? "20px 18px" : "26px 28px" }}>
          <h2 style={{ fontSize: isMobile ? 21 : 25, fontWeight: 900, color: NAVY, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
            {t('abaResources.startHereTitle')}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.6, margin: "0 0 16px" }}>
            {t('abaResources.startHereIntro')}
          </p>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
            {START_HERE.map((it, i) => (
              <li key={it.to}>
                <SeoLink to={it.to} style={{ display: "flex", gap: 12, alignItems: "flex-start", height: "100%", boxSizing: "border-box", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "14px 16px" }}>
                  <span style={{ width: 28, height: 28, borderRadius: "50%", background: INDIGO, color: "#fff", fontWeight: 800, fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
                  <span>
                    <span style={{ display: "block", fontSize: 14.5, fontWeight: 800, color: NAVY, marginBottom: 3 }}>{t(it.labelKey)}</span>
                    <span style={{ display: "block", fontSize: 13, color: TEXT_MID, lineHeight: 1.55 }}>{t(it.bodyKey)}</span>
                  </span>
                </SeoLink>
              </li>
            ))}
          </ol>
        </section>
      </div>

      {/* ── Grouped resource links ─────────────────────────────── */}
      <div style={{ padding: sectionPad, maxWidth: maxW, margin: "0 auto" }}>
        {GROUPS.map((g) => (
          <section key={g.titleKey} style={{ marginBottom: 40 }}>
            <h2 style={{ fontSize: isMobile ? 19 : 22, fontWeight: 900, color: NAVY, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
              {t(g.titleKey)}
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14 }}>
              {g.items.map((it) => (
                <SeoLink key={it.to} to={it.to} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <it.Icon size={18} color={INDIGO} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(it.labelKey)}</div>
                    <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(it.bodyKey)}</div>
                  </div>
                </SeoLink>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* ── Final CTA ────────────────────────────────────────── */}
      <section style={{ background: `linear-gradient(135deg, ${INDIGO} 0%, #7C3AED 100%)`, padding: isMobile ? "48px 18px" : "64px 48px", textAlign: "center" }}>
        <h2 style={{ fontSize: isMobile ? 25 : 33, fontWeight: 900, color: "#fff", margin: "0 0 12px", letterSpacing: "-0.025em" }}>
          {t('abaResources.ctaTitle')}
        </h2>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.82)", maxWidth: 520, margin: "0 auto 28px", lineHeight: 1.6 }}>
          {t('abaResources.ctaSubtitle')}
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: "#fff", color: INDIGO, border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 22px rgba(0,0,0,0.2)" }}>
            <BellIcon size={16} color={INDIGO} /> {t('abaResources.ctaButton')}
          </button>
        </div>
      </section>

      <GuideReviewStamp slug="aba-resources" />
      <PublicFooter />
    </div>
  );
}
